import { useMutation } from "@apollo/client";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Page, TextField, Button } from "@towersystems/roam-common-native";
import { Terminal } from "@towersystems/roam-common/lib/generated-types";
import { useEffect, useMemo, useState } from "react";
import { TerminalSettingsRootParamList } from "../../../../types";
import { useTerminalModelContextProvider } from "../../../../utilities/terminal";
import { MUTATION_UPDATE_TERMINAL } from "../../../../utilities/terminal/constants";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";

export type TerminalSettingsLandingScreenProps = NativeStackScreenProps<
  TerminalSettingsRootParamList,
  "EditTerminalNameScreen"
>;

export const EditTerminalNameScreen = ({
  navigation,
}: TerminalSettingsLandingScreenProps) => {
  const shouldFocus = useHasTransitionEnded(navigation);
  const database = useDatabase();
  const { terminal } = useTerminalModelContextProvider();

  const [value, setValue] = useState(terminal.name);

  const [f] = useMutation<{ updateTerminal: Terminal }>(
    MUTATION_UPDATE_TERMINAL
  );

  const handleOnSave = async () => {
    if (!isDirty || value.length == 0) {
      return;
    }
    return f({
      variables: {
        input: {
          id: terminal.id,
          name: value,
        },
      },
    })
      .then((r) => {
        if (r.data?.updateTerminal) {
          return database.write(async () => {
            await terminal.update((t) => {
              t.name = r.data?.updateTerminal.name || "UNKNOWN";
            });
          });
        }
      })
      .then(() => navigation.goBack())
      .catch(console.log);
  };

  const isDirty = useMemo(() => {
    return value !== terminal.name;
  }, [terminal.name, value]);

  useEffect(() => {
    const disabled = !isDirty || value === "";
    navigation.setOptions({
      headerRight: () => {
        return (
          <Button
            label="Save"
            isAction
            disabled={disabled}
            onPress={handleOnSave}
            size="compact"
          ></Button>
        );
      },
    });
  }, [isDirty, handleOnSave, isDirty]);

  return (
    <Page>
      <TextField
        placeholder="Terminal Name"
        value={value}
        onChangeText={(v) => setValue(v)}
        autoFocus={shouldFocus}
        validationMessage={
          value.length >= 1 ? undefined : "Please enter a name"
        }
      />
    </Page>
  );
};
